import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLoadTypeContentData } from "../../../../Utils/customHooks/UpToDateContent/useLoadTypeContent";
import { getContentFeedDateWindowFromComponent } from "../../../../Screens/DashboardView/StayUpToDate/AddComponentPicker/contentFeedTimeframe";
import { StayFeedLoadingPlaceholder } from "../../../../Screens/DashboardView/MasterTemplateEditor/WebsiteLibFeedSkeleton";
import { debounce } from "lodash";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  handleFirstImage,
  handleNavigate,
  updateIsEmptyStatus,
} from "../../../../Utils/StayUpToDate/RenderComponent";
import { addFeedScrollListener, getFeedScrollMetrics } from "../../../../Utils/feedScrollRoot";

import { useDomLoaded } from "../../../../hooks/DomLoadedContext";
import FrontendTemplate from "../StructureTemplates/FrontendTemplate";
import PostTemplate from "../CommonComponents/PostTemplate";
import { FeedSectionEmptyState } from "../FeedSectionEmptyState";
import { websiteEditorFeedEmptyMessage } from "../../../../Screens/DashboardView/MasterTemplateEditor/websiteEditorFeedEmptyMessage";

const FeaturedData = (props: any) => {
  const platform = props.platform || "featured";
  const type = props.type || "FEATURE";
  const itemsPerPage = 3;

  const param = useParams();

  const {
    user_id,
    username,
    title,
    handleRedirect,
    brand_color,
    isHandleNavigate,
    component,
    // handleShop,
    isLinks,
    removedPostIds,
    RenderUI,
    from,
    templateGridLoaderProps,
    designTokens,
    expandedSlot,
    gridSkeletonColumnCount = 8,
    isMobileView,
    showEmptyInEditor = false,
  } = props;
  const contentDateWindow = useMemo(
    () => getContentFeedDateWindowFromComponent(component),
    [component?.details, component?.id],
  );
  const {
    isLoading: PodcastDataLoading,
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  }: any = useLoadTypeContentData(
    username,
    user_id,
    platform,
    type,
    removedPostIds,
    component?.content_order,
    component?.custom_order?.split(","),
    component?.shoppable_content_filter,
    component?.pinned_posts?.split(",") || [],
    component?.shoppable_status_filter || "all",
    "default",
    "default",
    true,
    contentDateWindow,
  );

  const featuredItems = useMemo(() => {
    if (!data?.pages || !Array.isArray(data.pages)) {
      return [];
    }
    return data.pages.reduce((acc: any[], page: any) => {
      if (Array.isArray(page?.data)) {
        acc.push(...page.data);
      }
      return acc;
    }, []);
  }, [data?.pages]);

  const totalCount = data?.pages?.[0]?.total_count ?? 0;
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<any>(null);
  const isButtonClicked = useRef(false);
  const fetchLockRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const domLoaded = useDomLoaded();
  const handleScroll = useCallback(
    debounce(() => {
      if (!isButtonClicked.current) {
        if (containerRef.current && featuredItems.length) {
          const scrollLeft = containerRef.current.scrollLeft;
          const itemWidth =
            containerRef.current.scrollWidth / featuredItems.length;
          const newCurrentIndex = Math.round(scrollLeft / itemWidth);
          setCurrentIndex(newCurrentIndex);
        }
      }
    }, 100),
    [featuredItems.length]
  );
  useEffect(() => {
    const checkEmptyStatus = async () => {
      if (!PodcastDataLoading) {
        if (!data || totalCount === 0) {
          if (!showEmptyInEditor) {
            await updateIsEmptyStatus(title, user_id, component.id, from);
          }
          // setIsLoading(false);
        } else {
          await handleFirstImage(
            title,
            component?.id,
            featuredItems?.[0],
            user_id,
            from
          );
          // setIsLoading(false);
        }
      }
    };
    checkEmptyStatus();
  }, [
    PodcastDataLoading,
    data,
    featuredItems,
    totalCount,
    user_id,
    component?.id,
    title,
  ]);
  useEffect(() => {
    if (containerRef.current && featuredItems.length) {
      const itemWidth = containerRef.current.scrollWidth / featuredItems.length;
      const scrollPosition = itemWidth * currentIndex;
      containerRef.current.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    }
  }, [currentIndex, featuredItems]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.addEventListener("scroll", handleScroll);
      return () => {
        if (containerRef.current) {
          containerRef.current.removeEventListener("scroll", handleScroll);
        }
      };
    }
  }, [featuredItems, handleScroll]);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;

    let ticking = false;

    const handleWindowScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const { scrollPercentage } = getFeedScrollMetrics();

          if (scrollPercentage >= 70) {
            if (hasNextPage && !isFetchingNextPage) {
              fetchNextPage();
            }
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    return addFeedScrollListener(handleWindowScroll);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (
      typeof fetchNextPage === "function" &&
      hasNextPage &&
      !isFetchingNextPage &&
      featuredItems.length > 0
    ) {
      const itemsLeft = featuredItems.length - (currentIndex + itemsPerPage);
      // Unlock when we have enough items ahead
      if (itemsLeft > itemsPerPage) {
        fetchLockRef.current = false;
      }

      // Only fetch once per boundary crossing
      if (itemsLeft <= itemsPerPage && !fetchLockRef.current) {
        fetchLockRef.current = true;
        fetchNextPage();
      }
    }
  }, [
    currentIndex,
    featuredItems.length,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    itemsPerPage,
  ]);

  const handlePrevPage = () => {
    isButtonClicked.current = true;
    setCurrentIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    setTimeout(() => {
      isButtonClicked.current = false;
    }, 500);
  };

  const handleNextPage = () => {
    isButtonClicked.current = true;
    setCurrentIndex((prevIndex) =>
      Math.min(prevIndex + 1, Math.max(featuredItems.length - itemsPerPage, 0))
    );
    setTimeout(() => {
      isButtonClicked.current = false;
    }, 500);
  };

  return (
    <div className="reels-wrapper">
      {(PodcastDataLoading && !isFetchingNextPage) || !domLoaded ? (
        <>
          <StayFeedLoadingPlaceholder
            arrayLength={RenderUI ? gridSkeletonColumnCount : 5}
              individualGrid={Boolean(RenderUI) && !expandedSlot}
              expandedSlot={expandedSlot}
              from={from}
              component={component}
              designTokens={designTokens}
            templateGridLoaderProps={templateGridLoaderProps}
          />
        </>
      ) : featuredItems && featuredItems.length === 0 ? (
        showEmptyInEditor ? (
          <FeedSectionEmptyState
            title={title}
            designTokens={designTokens}
            message={
              String(type || "").toLowerCase().includes("trending products")
                ? websiteEditorFeedEmptyMessage("trending-products")
                : String(type || "").toLowerCase().includes("trending")
                  ? websiteEditorFeedEmptyMessage("trending-content")
                  : websiteEditorFeedEmptyMessage("posts")
            }
          />
        ) : null
      ) : RenderUI ? (
        <RenderUI
          data={featuredItems}
          currentIndex={currentIndex}
          handleNavigate={handleNavigate}
          component={component}
          handleNextPage={handleNextPage}
          handlePrevPage={handlePrevPage}
          containerRef={containerRef}
          handleRedirect={handleRedirect}
          brand_color={brand_color}
          title={title}
          type={type}
          isLinks={isLinks}
          from={from}
          username={username}
          user_id={user_id}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          expandedSlot={expandedSlot}
          designTokens={designTokens}
          isMobileView={isMobileView}
        />
      ) : (
        <>
          {featuredItems && featuredItems.length > 0 ? (
            <PostTemplate
              processedData={featuredItems}
              total_count={totalCount}
              component={component}
              user_id={user_id}
              username={username}
              handleRedirect={handleRedirect}
              brand_color={brand_color}
              title={title}
              from={from}
              type={type}
              itemsPerPage={itemsPerPage}
              platform={platform}
              pinnedPostIds={component?.pinned_posts?.split(",") || []}
              isLinks={isLinks}
              designTokens={designTokens}
            />
          ) : (
            ""
          )}
        </>
      )}
    </div>
  );
};

export default FeaturedData;
